import { WMButton, WMButtonVariant, WMTextField } from '@walkme/wm-ui';
import React, { useCallback, useMemo } from 'react';

import classes from './TimeSelection.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const keyPrefix = 'components.editNotification.editNotificationContent.sendStepDrawer.dateTimePicker.timeSelection';

export enum DayParts {
    AM = 'AM',
    PM = 'PM'
}

export default function TimeSelection({ date, onChange }: { date: Date; onChange: (date: Date) => void }): JSX.Element {
    const { t } = useTranslation('', { keyPrefix });
    const selectedDayPart = useMemo(() => (date.getHours() > 11 ? DayParts.PM : DayParts.AM), [date]);

    const display2Digits = (value: number) => {
        if (value < 10) {
            return `0${value}`;
        }
        return `${value}`;
    };

    const hoursValue = useMemo(() => display2Digits(date.getHours() % 12 || 12), [date]);
    const minutesValue = useMemo(() => display2Digits(date.getMinutes()), [date]);

    const onHoursChange = useCallback<React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
        event => {
            const newDate = new Date(date);
            let hours = +event.target.value;
            if (!hours || hours < 1 || hours > 12) hours = 12;
            hours = selectedDayPart === DayParts.AM ? hours % 12 : (hours + 12) % 24 || 12;
            newDate.setHours(hours);
            onChange(newDate);
        },
        [date, onChange, selectedDayPart]
    );

    const onMinutesChange = useCallback<React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
        event => {
            let minutes = +event.target.value;
            if (!minutes || minutes > 59 || minutes < 0) minutes = 0;
            const newDate = new Date(date);
            newDate.setMinutes(minutes);
            onChange(newDate);
        },
        [date, onChange]
    );

    const onDayPartChange = useCallback(
        (dayPart: DayParts) => {
            let hours = date.getHours();
            if (dayPart === DayParts.AM && hours >= 12) hours -= 12;
            if (dayPart === DayParts.PM && hours < 12) hours += 12;
            const newDate = new Date(date);
            newDate.setHours(hours);
            onChange(newDate);
        },
        [onChange, date]
    );

    return (
        <div className={classes.timeSelectionWrapper}>
            <WMTextField
                className={classes.timeField}
                InputProps={{ classes: { root: classes.timeFieldInput }, inputProps: { min: 1, max: 12 } }}
                type="number"
                value={hoursValue}
                onChange={onHoursChange}
            />

            <WMTextField
                className={classes.timeField}
                type="number"
                value={minutesValue}
                InputProps={{
                    classes: { root: classes.timeFieldInput },
                    inputProps: { min: 0, max: 59 }
                }}
                onChange={onMinutesChange}
            />

            <div className={classes.amPmSelect}>
                <WMButton
                    className={clsx(classes.amPmBtn, { [classes.selected]: selectedDayPart === DayParts.AM })}
                    variant={WMButtonVariant.Text}
                    onClick={() => onDayPartChange(DayParts.AM)}
                >
                    {t('am')}
                </WMButton>

                <WMButton
                    className={clsx(classes.amPmBtn, { [classes.selected]: selectedDayPart === DayParts.PM })}
                    variant={WMButtonVariant.Text}
                    onClick={() => onDayPartChange(DayParts.PM)}
                >
                    {t('pm')}
                </WMButton>
            </div>
        </div>
    );
}
